import React, { Suspense, useEffect, useState } from 'react'
import { GuestLanguage, GuestPageConfig, loadGuestPageConfig, trackGuestView } from './guestTracking'

// The download box and the offers are downloaded only when switched on, so today's page loads
// exactly what it did before.
export const GuestDownloadBox = React.lazy(() => import('./GuestDownloadBox'))
export const GuestPartnerOffers = React.lazy(() => import('./GuestPartnerOffers'))

export function guestLanguage(language: string | undefined): GuestLanguage {
  return language === 'en' ? 'en' : 'ar'
}

/**
 * The guest page's switches, read once the invitation has loaded (so it never competes with it), and
 * invitation_viewed once they say events are on. null while loading, on any failure, or when off.
 */
export function useGuestPage(eventId: number | undefined, language: string | undefined): GuestPageConfig | null {
  const [config, setConfig] = useState<GuestPageConfig | null>(null)

  useEffect(() => {
    if (!eventId) return
    let alive = true
    loadGuestPageConfig().then((loaded) => {
      if (alive) setConfig(loaded)
    })
    return () => {
      alive = false
    }
  }, [eventId])

  useEffect(() => {
    if (config && config.events && eventId) {
      trackGuestView({ name: 'invitation_viewed', event_id: eventId, language: guestLanguage(language) })
    }
    // The view is counted once, in the language the page opened in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, eventId])

  return config
}

/** Whether the guest has just confirmed in this visit: their status turned to going (1) after it loaded. */
export function useJustConfirmed(status: number | null | undefined): boolean {
  const [justConfirmed, setJustConfirmed] = useState(false)
  const last = React.useRef<number | null | undefined>(undefined)

  useEffect(() => {
    if (status === undefined) return
    if (last.current !== undefined && last.current !== 1 && status === 1) setJustConfirmed(true)
    // Changed their answer again (declined): back to the invitation's own box.
    if (status !== 1) setJustConfirmed(false)
    last.current = status
  }, [status])

  return justConfirmed
}

/**
 * Where a guest-page part renders: nothing while it downloads, and nothing at all if it fails to
 * download or throws, so the invitation itself is never affected.
 */
export class GuestSlot extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch() {
    // Never shown to the guest.
  }

  render() {
    if (this.state.failed) return null
    return <Suspense fallback={null}>{this.props.children}</Suspense>
  }
}
